import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { addToastAlert } from 'actions/toastAlertActions';
import Alert from 'constants/alert-messages';

/**
 * Custom hook to handle email validation logic for Add User Modal.
 * Manages email lookup states, user existence checks, and form progression.
 *
 * @param {Object} params - Validation parameters
 * @param {boolean} params.userEmailLookupLoaded - Email lookup completed
 * @param {boolean} params.userEmailLookupLoading - Email lookup in progress
 * @param {string|null} params.userEmailLookupError - Email lookup error
 * @param {Object} params.userEmailLookup - Email lookup result
 * @param {boolean} params.userExists - Customer portal user exists
 * @param {boolean} params.userExistsLoaded - Customer user check completed
 * @param {boolean} params.userExistsLoading - Customer user check in progress
 * @param {string|null} params.userExistsError - Customer user check error
 * @param {boolean} params.adminUserExists - Admin portal user exists
 * @param {boolean} params.adminUserExistsLoaded - Admin user check completed
 * @param {boolean} params.adminUserExistsLoading - Admin user check in progress
 * @param {string|null} params.adminUserExistsError - Admin user check error
 * @param {string} params.addUserDetailsFormId - Form ID for user details step
 *
 * @returns {Object} Validation state
 * @returns {boolean} addUserDetailsNextButtonDisabled - Whether next button should be disabled
 * @returns {string} formId - Current form ID
 */
export const useEmailValidation = ({
  userEmailLookupLoaded,
  userEmailLookupLoading,
  userEmailLookupError,
  userEmailLookup,
  userExists,
  userExistsLoaded,
  userExistsLoading,
  userExistsError,
  adminUserExists,
  adminUserExistsLoaded,
  adminUserExistsLoading,
  adminUserExistsError,
  addUserDetailsFormId,
}) => {
  const dispatch = useDispatch();
  const emailLookupFormId = 'email-lookup-form';

  const [addUserDetailsNextButtonDisabled, setAddUserDetailsNextButtonDisabled] = useState(false);
  const [formId, setFormId] = useState(emailLookupFormId);

  useEffect(() => {
    if (userEmailLookupLoaded && (userExistsLoaded || adminUserExistsLoaded)) {
      // Check if the user exists
      if (userEmailLookup?.user && Object.keys(userEmailLookup?.user).length !== 0 && (userExists || adminUserExists)) {
        dispatch(addToastAlert(Alert.EMAIL_REGISTERED, 'error'));
        setAddUserDetailsNextButtonDisabled(true);
      } else {
        dispatch(addToastAlert(Alert.EMAIL_VALID, 'success'));
        setFormId(addUserDetailsFormId);
        setAddUserDetailsNextButtonDisabled(false);
      }
    } else if (userEmailLookupError || userExistsError || adminUserExistsError) {
      dispatch(addToastAlert(Alert.EMAIL_INVALID, 'error'));
    }
  }, [
    userEmailLookupLoading,
    userEmailLookupLoaded,
    userEmailLookupError,
    userExistsLoaded,
    userExistsLoading,
    userExistsError,
    adminUserExistsLoaded,
    adminUserExistsLoading,
    adminUserExistsError,
    userEmailLookup,
    userExists,
    adminUserExists,
    addUserDetailsFormId,
    dispatch,
  ]);

  return {
    addUserDetailsNextButtonDisabled,
    formId,
    setFormId,
  };
};
